import type { IUserInfo } from "@/hooks/use-user-info";
import httpClient from "@/services/index";
import type { Notification, Recommendation } from "@/model/notification";
import { NotificationStatus } from "@/constants";
import { API_ENDPOINTS } from "@/services/endpoints";
import { RECOMMENDATION_MODULE } from "@/types/notification";
import type {
  RecommendationModule,
  RecommendationStatusRequestData,
  RecommendationStatusResponseData,
} from "@/types/notification";
import { lowerCaseComparison } from "@/lib/string-util";

export const notifications = async (userInfo: IUserInfo) => {
  const url = API_ENDPOINTS.NOTIFICATION.LIST;
  const params = {
    loginGroupId: userInfo?.loginGroupId,
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
    username: userInfo?.username,
    planId: userInfo?.planId,
  };
  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "3.0",
    },
  };
  return httpClient
    .post(url, params, config)
    .then((res) => res?.data?.data || []);
};

export const recommendations = async (userInfo: IUserInfo) => {
  const url = API_ENDPOINTS.RECOMMENDATION.LIST;
  const params = {
    loginGroupId: userInfo?.loginGroupId,
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    divisionId: userInfo?.divisionId,
  };
  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
    },
  };
  return httpClient
    .post(url, params, config)
    .then((res) => res?.data?.data || []);
};

export const updateNotificationStatus = async (
  userInfo: IUserInfo,
  notification: Notification,
  status: NotificationStatus,
) => {
  const params = {
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    username: userInfo?.username,
    messageId: notification?.messageId,
    status,
  };
  const url = API_ENDPOINTS.NOTIFICATION.UPDATE_STATUS;
  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "2.0",
    },
  };
  return httpClient.put(url, params, config).then((res) => res.data);
};

export const updateRecommendationStatus = async (
  userInfo: IUserInfo,
  recommendation: Recommendation,
  status: NotificationStatus,
): Promise<RecommendationStatusResponseData> => {
  const module: RecommendationModule =
    lowerCaseComparison(status, NotificationStatus.LIKE) ||
    lowerCaseComparison(status, NotificationStatus.UNLIKE)
      ? RECOMMENDATION_MODULE.LIKE
      : RECOMMENDATION_MODULE.SAVE;
  const value =
    lowerCaseComparison(status, NotificationStatus.LIKE) ||
    lowerCaseComparison(status, NotificationStatus.SAVE);

  const payload: RecommendationStatusRequestData = {
    loginGroupId: userInfo?.loginGroupId,
    memberId: userInfo?.memberId,
    groupId: userInfo?.groupId,
    module,
    cohortId: recommendation?.cohortId,
    value,
  };
  const url = API_ENDPOINTS.RECOMMENDATION.UPDATE_STATUS;
  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.accessToken}`,
      "Content-Type": "application/json",
      "Accept-Version": "1.0",
    },
  };
  return httpClient.put(url, payload, config).then((res) => res.data);
};
